interface ToothData {
	toothNumber: number;
	values: Record<string, string>;
}

interface OdontogramField {
	id: string;
	type: string;
	label: string;
	options?: string[];
}

interface PdfOdontogramProps {
	teethData?: ToothData[];
	odontogramFields?: OdontogramField[];
	emptyMessage?: string;
}

const quadrant1 = [18, 17, 16, 15, 14, 13, 12, 11];
const quadrant2 = [21, 22, 23, 24, 25, 26, 27, 28];
const quadrant3 = [31, 32, 33, 34, 35, 36, 37, 38];
const quadrant4 = [48, 47, 46, 45, 44, 43, 42, 41];

const toothSize = "3.5mm";
const fontSize = "5pt";

function ToothCircle({
	number,
	selected,
}: {
	number: number;
	selected: boolean;
}) {
	return (
		<div
			style={{
				width: toothSize,
				height: toothSize,
				borderRadius: "50%",
				border: selected ? "1pt solid #3b82f6" : "0.5pt dashed #d1d5db",
				backgroundColor: selected ? "#dbeafe" : "transparent",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				fontSize: "3pt",
				color: selected ? "#1e40af" : "#9ca3af",
				fontWeight: selected ? "bold" : "normal",
			}}
		>
			{number}
		</div>
	);
}

function QuadrantRow({
	teeth,
	selectedNumbers,
}: {
	teeth: number[];
	selectedNumbers: number[];
}) {
	return (
		<div style={{ display: "flex", gap: "0.5mm" }}>
			{teeth.map((t) => (
				<ToothCircle
					key={t}
					number={t}
					selected={selectedNumbers.includes(t)}
				/>
			))}
		</div>
	);
}

export const PdfOdontogram: React.FC<PdfOdontogramProps> = ({
	teethData,
	odontogramFields,
	emptyMessage,
}) => {
	const selectedNumbers = teethData?.map((t) => t.toothNumber) ?? [];
	const hasData = selectedNumbers.length > 0;
	const hasFields = odontogramFields && odontogramFields.length > 0;
	const teethWithInfo =
		teethData?.filter((t) =>
			Object.values(t.values).some((v) => v !== "")
		) ?? [];

	return (
		<div style={{ fontSize }}>
			<div
				style={{
					fontSize: "7pt",
					fontWeight: "bold",
					color: "#000000",
					marginBottom: "1mm",
					textTransform: "uppercase",
					letterSpacing: "0.3pt",
				}}
			>
				Odontogramm
			</div>

			<div
				style={{
					border: "0.5pt solid #e5e7eb",
					borderRadius: "1mm",
					padding: "2mm",
					backgroundColor: "#fafafa",
				}}
			>
				<div
					style={{
						display: "flex",
						gap: "1mm",
						justifyContent: "center",
					}}
				>
					<QuadrantRow
						teeth={quadrant1}
						selectedNumbers={selectedNumbers}
					/>
					<div
						style={{
							width: "1mm",
							borderLeft: "0.5pt solid #d1d5db",
						}}
					/>
					<QuadrantRow
						teeth={quadrant2}
						selectedNumbers={selectedNumbers}
					/>
				</div>
				<div
					style={{
						borderTop: "0.5pt solid #d1d5db",
						margin: "1mm 0",
					}}
				/>
				<div
					style={{
						display: "flex",
						gap: "1mm",
						justifyContent: "center",
					}}
				>
					<QuadrantRow
						teeth={quadrant4}
						selectedNumbers={selectedNumbers}
					/>
					<div
						style={{
							width: "1mm",
							borderLeft: "0.5pt solid #d1d5db",
						}}
					/>
					<QuadrantRow
						teeth={quadrant3}
						selectedNumbers={selectedNumbers}
					/>
				</div>
			</div>

			{!hasData && emptyMessage && (
				<div
					style={{
						fontSize: "6pt",
						color: "#9ca3af",
						fontStyle: "italic",
						textAlign: "center",
						marginTop: "2mm",
					}}
				>
					{emptyMessage}
				</div>
			)}

			{hasData && hasFields && teethWithInfo.length > 0 && (
				<table
					style={{
						width: "100%",
						borderCollapse: "collapse",
						fontSize: "6pt",
						marginTop: "2mm",
					}}
				>
					<thead>
						<tr>
							<th
								style={{
									textAlign: "left",
									padding: "0.5mm 1mm",
									borderBottom: "0.5pt solid #d1d5db",
									fontWeight: "bold",
									color: "#6b7280",
								}}
							>
								Zahn
							</th>
							{odontogramFields!.map((f) => (
								<th
									key={f.id}
									style={{
										textAlign: "left",
										padding: "0.5mm 1mm",
										borderBottom: "0.5pt solid #d1d5db",
										fontWeight: "bold",
										color: "#6b7280",
									}}
								>
									{f.label}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{teethWithInfo
							.sort((a, b) => a.toothNumber - b.toothNumber)
							.map((tooth) => (
								<tr key={tooth.toothNumber}>
									<td
										style={{
											padding: "0.5mm 1mm",
											borderBottom: "0.3pt solid #e5e7eb",
											fontWeight: "bold",
											color: "#374151",
										}}
									>
										{tooth.toothNumber}
									</td>
									{odontogramFields!.map((f) => {
										let displayValue =
											tooth.values[f.id] || "—";
										if (
											f.type === "select" &&
											f.options &&
											displayValue !== "—"
										) {
											const idx = parseInt(
												displayValue,
												10
											);
											if (!isNaN(idx) && f.options[idx]) {
												displayValue = f.options[idx];
											}
										}
										return (
											<td
												key={f.id}
												style={{
													padding: "0.5mm 1mm",
													borderBottom:
														"0.3pt solid #e5e7eb",
													color: "#374151",
												}}
											>
												{displayValue}
											</td>
										);
									})}
								</tr>
							))}
					</tbody>
				</table>
			)}
		</div>
	);
};
